import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateCompany,
  useUpdateCompany,
} from '@/services/api/endpoints/CompanyService';
import type { Company } from '@/types';

interface CompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  logoUrl: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  logoUrl?: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  address: '',
  logoUrl: '',
};

export function CompanyModal({ open, onOpenChange, company }: CompanyModalProps) {
  const { t } = useTranslation();
  const isEditMode = company !== null;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});

  const createMutation = useCreateCompany();
  const updateMutation = useUpdateCompany();

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (open) {
      if (company) {
        setFormData({
          name: company.name || '',
          email: company.email || '',
          phone: company.phone || '',
          address: company.address || '',
          logoUrl: company.logoUrl || '',
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [open, company]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('companies.validation.nameRequired');
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('companies.validation.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload: Partial<Company> = {
      name: formData.name.trim(),
    };

    if (formData.email.trim()) payload.email = formData.email.trim();
    if (formData.phone.trim()) payload.phone = formData.phone.trim();
    if (formData.address.trim()) payload.address = formData.address.trim();
    if (formData.logoUrl.trim()) payload.logoUrl = formData.logoUrl.trim();

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: company.id, ...payload });
        toast.success(t('companies.toast.updateSuccess'));
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(t('companies.toast.createSuccess'));
      }
      onOpenChange(false);
    } catch {
      toast.error(
        isEditMode
          ? t('companies.toast.updateError')
          : t('companies.toast.createError')
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t('companies.editCompany') : t('companies.createCompany')}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? t('companies.editCompanyDescription')
              : t('companies.createCompanyDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="company-name">
              {t('companies.name')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="company-name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
              placeholder={t('companies.name')}
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="company-email">{t('companies.email')}</Label>
            <Input
              id="company-email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              placeholder={t('companies.email')}
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="company-phone">{t('companies.phone')}</Label>
            <Input
              id="company-phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              error={errors.phone}
              placeholder={t('companies.phone')}
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="company-address">{t('companies.address')}</Label>
            <Input
              id="company-address"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              error={errors.address}
              placeholder={t('companies.address')}
            />
          </div>

          {/* Logo URL */}
          <div className="space-y-2">
            <Label htmlFor="company-logo">{t('companies.logoUrl')}</Label>
            <Input
              id="company-logo"
              value={formData.logoUrl}
              onChange={(e) => handleChange('logoUrl', e.target.value)}
              error={errors.logoUrl}
              placeholder="https://..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting}>
            {isEditMode ? t('common.save') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
